// Jurisdiction constants — the seed of `client.jurisdictionId`
// (jurisdiction-architecture.md §6/§8, Stephanie #9). Reusable across the signup
// selector, the About You address dropdown, and future jurisdiction-pack logic.
//
// SEAM HYGIENE (§6): do NOT hardcode a single province ("ON") in features —
// resolve through this list + the client's jurisdiction_country /
// jurisdiction_province columns.

export type Province = { code: string; name: string };

/** All 13 Canadian provinces and territories, alphabetical by name. */
export const CA_PROVINCES: Province[] = [
  { code: "AB", name: "Alberta" },
  { code: "BC", name: "British Columbia" },
  { code: "MB", name: "Manitoba" },
  { code: "NB", name: "New Brunswick" },
  { code: "NL", name: "Newfoundland and Labrador" },
  { code: "NS", name: "Nova Scotia" },
  { code: "NT", name: "Northwest Territories" },
  { code: "NU", name: "Nunavut" },
  { code: "ON", name: "Ontario" },
  { code: "PE", name: "Prince Edward Island" },
  { code: "QC", name: "Quebec" },
  { code: "SK", name: "Saskatchewan" },
  { code: "YT", name: "Yukon" },
];

export type Country = { code: string; name: string; hasProvinces: boolean };

/**
 * Supported countries for the signup selector. Canada-first; this list is the
 * single extension point as jurisdiction packs expand. `hasProvinces` gates the
 * region dropdown (only CA has a province list today).
 */
export const COUNTRIES: Country[] = [
  { code: "CA", name: "Canada", hasProvinces: true },
  { code: "US", name: "United States", hasProvinces: false },
  { code: "GB", name: "United Kingdom", hasProvinces: false },
  { code: "AU", name: "Australia", hasProvinces: false },
];

export const DEFAULT_COUNTRY = "CA";

/** Flag emoji by country code, with a globe fallback for anything not yet packed. */
const COUNTRY_FLAGS: Record<string, string> = {
  CA: "🇨🇦",
  US: "🇺🇸",
  GB: "🇬🇧",
  AU: "🇦🇺",
};

export function countryFlag(code: string | null | undefined): string {
  return (code && COUNTRY_FLAGS[code]) || "🌐";
}

export function countryHasProvinces(code: string | null | undefined): boolean {
  return COUNTRIES.find((c) => c.code === code)?.hasProvinces ?? false;
}

/** Full province/territory name for a 2-letter code (e.g. "ON" → "Ontario"); "" if unknown. */
export function provinceName(code: string | null | undefined): string {
  return CA_PROVINCES.find((p) => p.code === code)?.name ?? "";
}

/** Full country name for a code (e.g. "CA" → "Canada"); falls back to the code itself. */
export function countryName(code: string | null | undefined): string {
  return COUNTRIES.find((c) => c.code === code)?.name ?? (code ?? "");
}
